import React, { useState, useEffect } from 'react';
import { X, Copy, Check, Share2, Mail, Twitter, MessageCircle } from 'lucide-react';
import './ShareMapModal.css';

const ShareMapModal = ({ isOpen, onClose, routeData, searchMetadata }) => {
    const [shortLink, setShortLink] = useState('');
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (isOpen && routeData) {
            generateShortLink();
        } else {
            setShortLink('');
            setCopied(false);
        }
    }, [isOpen, routeData]);

    const generateShortLink = async () => {
        setLoading(true);
        
        // 1. Construct the robust Long URL first (Fallback)
        const baseUrl = window.location.origin + '/shared-route';
        const params = new URLSearchParams(window.location.search);
        
        // Encode the specific route data into the URL to ensure robust sharing
        if (routeData) {
            try {
                const jsonStr = JSON.stringify(routeData);
                const encodedData = btoa(unescape(encodeURIComponent(jsonStr)));
                params.set('rdata', encodedData);
            } catch (e) {
                console.warn("Route encoding failed:", e);
            }
        }

        const fullUrl = `${baseUrl}?${params.toString()}`;

        try {
            // 2. Try to get a short link from API
            const response = await fetch('https://f2qq5dfp52.execute-api.ap-south-1.amazonaws.com/default/create_short_link', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type: 'map',
                    url: fullUrl
                })
            });

            const data = await response.json();
            if (data && data.data && data.data.short_url) {
                setShortLink(data.data.short_url);
            } else {
                // API returned success but no URL, use Long URL
                setShortLink(fullUrl);
            }
        } catch (error) {
            console.error('Shortener API Error:', error);
            // 3. Robust Fallback: If API fails, use the working Long URL
            setShortLink(fullUrl);
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(shortLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const shareSocial = (platform) => {
        const text = "Check out this travel route on RouteChef!";
        let url = '';

        switch (platform) {
            case 'whatsapp':
                url = `https://wa.me/?text=${encodeURIComponent(text + ' ' + shortLink)}`;
                break;
            case 'twitter':
                url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shortLink)}`;
                break;
            case 'gmail':
                url = `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(shortLink)}`;
                break;
            default:
                break;
        }

        if (url) window.open(url, '_blank');
    };

    if (!isOpen) return null;

    return (
        <div className="share-modal-overlay" onClick={onClose}>
            <div className="share-modal-content" onClick={e => e.stopPropagation()}>
                <div className="share-modal-header">
                    <div className="share-header-left">
                        <Share2 size={20} className="share-icon-main" />
                        <h2>Share Route</h2>
                    </div>
                    <button className="share-close-btn" onClick={onClose} aria-label="Close share modal">
                        <X size={20} />
                    </button>
                </div>

                <div className="share-body">
                    <div className="route-preview-card">
                        <div className="route-preview-info">
                            <div className="preview-locations">
                                <span className="loc-text">{searchMetadata?.user_src_name || 'Origin'}</span>
                                <div className="preview-arrow">→</div>
                                <span className="loc-text">{searchMetadata?.user_dest_name || 'Destination'}</span>
                            </div>
                            <p className="preview-subtext">View detailed map and travel options</p>
                        </div>
                    </div>

                    <div className="link-section">
                        <label>Link to share</label>
                        <div className="link-input-group">
                            <input 
                                type="text" 
                                readOnly 
                                value={loading ? 'Generating link...' : shortLink} 
                                className="share-link-input"
                            />
                            <button className={`copy-link-btn ${copied ? 'copied' : ''}`} onClick={handleCopy}>
                                {copied ? (
                                    <>
                                        <Check size={16} />
                                        <span>COPIED</span>
                                    </>
                                ) : (
                                    <>
                                        <Copy size={16} />
                                        <span>COPY LINK</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="social-sharing">
                        <p className="social-label">Or share via</p>
                        <div className="social-buttons">
                            <button className="social-btn whatsapp" onClick={() => shareSocial('whatsapp')}>
                                <div className="social-icon-wrapper">
                                    <MessageCircle size={24} fill="currentColor" />
                                </div>
                                <span>WhatsApp</span>
                            </button>
                            <button className="social-btn twitter" onClick={() => shareSocial('twitter')}>
                                <div className="social-icon-wrapper">
                                    <Twitter size={24} fill="currentColor" />
                                </div>
                                <span>X</span>
                            </button>
                            <button className="social-btn gmail" onClick={() => shareSocial('gmail')}>
                                <div className="social-icon-wrapper">
                                    <Mail size={24} fill="currentColor" />
                                </div>
                                <span>Gmail</span>
                            </button>
                        </div>
                    </div>
                </div>
                
                <div className="share-footer">
                    <p>Powered by RouteChef Shortener</p>
                </div>
            </div>
        </div>
    );
};

export default ShareMapModal;
